import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { createSetting, fetchSettings, updateSetting } from "../../services/crmServices/settingService";

export function IPWhitelistModal({ open, onClose }) {
    const [whiteIps, setWhiteIps] = useState([""]);
    const [settings, setSettings] = useState({});
    
    useEffect(() => {
        if (open) {
            initModal();
        }
    }, [open]);

    const initModal = () => {
        fetchSettings().then((res) => {
            if (res.data?.length) {
                setSettings(res.data[0]);
                setWhiteIps(res.data[0].whiteIps || [""]);
            } else {
                setWhiteIps([""]);
            }
        });
    };

    const addIpField = () => {
        setWhiteIps([...whiteIps, ""]);
    };

    const removeIpField = (index) => {
        setWhiteIps(whiteIps.filter((_, i) => i !== index));
    };

    const updateWhiteIp = (index, value) => {
        const updated = [...whiteIps];
        updated[index] = value;
        setWhiteIps(updated);
    };

    const handleSave = async () => {
        const validIps = whiteIps.filter((ip) => ip.trim() !== "");
        if (Object.keys(settings).length) {
            await createSetting({ ...settings, whiteIps: validIps }).then((res) => {
                if (res.status === 200) {
                    toast.success("IP whitelist updated successfully!");
                    onClose();
                } else {
                    toast.error("Something went wrong. Try again later.");
                }
            });
        } else {
            await updateSetting({ ...settings, whiteIps: validIps }).then((res) => {
                if (res.status === 200) {
                    toast.success("IP whitelist updated successfully!");
                    onClose();
                } else {
                    toast.error("Something went wrong. Try again later.");
                }
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md bg-white border-gray-200">
                <DialogHeader className="border-b border-gray-200 pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900">
                        IP Whitelist Management
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Add IP addresses that are allowed to access the system.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-2">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 shadow-sm space-y-3">
                        <Label className="text-gray-900 font-semibold">IP Addresses</Label>
                    {whiteIps.map((ip, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <Input
                                value={ip}
                                onChange={(e) => updateWhiteIp(index, e.target.value)}
                                placeholder="192.168.1.1"
                                className="flex-1"
                            />
                            {whiteIps.length > 1 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={() => removeIpField(index)}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                            {index === whiteIps.length - 1 && (
                                <Button type="button" variant="outline" size="icon" onClick={addIpField}>
                                    <Plus className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    ))}
                    </div>
                </div>

                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <Button onClick={handleSave} className="flex-1 bg-blue-600 hover:bg-blue-700:bg-blue-600 text-white shadow-md hover:shadow-lg transition-all duration-200">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Save
                    </Button>
                    <Button variant="outline" onClick={onClose} className="flex-1">
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
